import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle2, AlertTriangle, XCircle, Sparkles, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  ai_drafting: "AI email drafting",
  sending: "Email sending pipeline",
  sourcing: "Lead sourcing",
  booking: "Meeting booking",
};

export default function GoLive() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Inline-edit state for one-click fixes
  const [senderFirst, setSenderFirst] = useState("");
  const [senderFull, setSenderFull] = useState("");
  const [vp, setVp] = useState("");
  const [suggestedVp, setSuggestedVp] = useState("");
  const [calMode, setCalMode] = useState("manual");
  const [bookingLink, setBookingLink] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/go-live/checklist");
      setData(data);
      // Fetch the user's actual current settings so the form pre-fills
      const cur = await api.get("/go-live/current-settings");
      setSenderFirst(cur.data.sender_first_name || "");
      setSenderFull(cur.data.sender_full_name || "");
      setVp(cur.data.value_proposition || "");
      // If booking_link already set, switch radio to that mode
      if (cur.data.booking_link) {
        setBookingLink(cur.data.booking_link);
        setCalMode("outlook");
      } else if (cur.data.calendar_mode === "manual") {
        setCalMode("manual");
      } else {
        setCalMode("manual");
      }
      const sug = await api.get("/go-live/suggested-value-prop");
      setSuggestedVp(sug.data.value_proposition);
    } catch (err) {
      toast.error("Could not load checklist");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const apply = async (body, successMsg) => {
    try {
      await api.post("/go-live/apply-defaults", body);
      toast.success(successMsg || "Saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  if (loading || !data) {
    return <Layout title="Go-Live Checklist"><div className="p-8 text-center text-muted-foreground">Loading…</div></Layout>;
  }

  const grouped = data.items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  const summary = data.summary;
  const ready = summary.blockers === 0;

  return (
    <Layout
      title="Go-Live Checklist"
      subtitle={ready ? "All systems green — you're ready to fly" : `${summary.blockers} blocker(s) to clear before full-auto cold-emailing`}
    >
      {/* Summary banner */}
      <div className={`mb-6 p-5 border ${ready ? "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10" : "border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10"}`} style={{ borderRadius: 2 }} data-testid="gl-summary">
        <div className="flex items-center gap-4">
          {ready ? <CheckCircle2 className="h-8 w-8 text-[hsl(var(--success))]" /> : <AlertTriangle className="h-8 w-8 text-[hsl(var(--warning))]" />}
          <div>
            <div className="font-serif text-xl" style={{ fontWeight: 450 }}>
              {ready ? "Engine is go-live ready" : "Engine needs setup"}
            </div>
            <div className="text-sm text-muted-foreground">
              <strong className="text-[hsl(var(--success))]">{summary.ready}</strong> ready · {" "}
              <strong className="text-[hsl(var(--warning))]">{summary.warnings}</strong> warnings · {" "}
              <strong className="text-destructive">{summary.blockers}</strong> blockers
              {data.warmup.mailbox_count > 0 && (
                <> · warmup: day {data.warmup.days}/21, lowest score {data.warmup.min_score}/100</>
              )}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="ml-auto" style={{ borderRadius: 2 }} data-testid="gl-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* AI Drafting block */}
      <Section title={CATEGORY_LABELS.ai_drafting} items={grouped.ai_drafting || []}>
        <div className="bg-card border border-border p-5 space-y-4" style={{ borderRadius: 2 }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Sender first name</Label>
              <Input value={senderFirst} onChange={(e) => setSenderFirst(e.target.value)} placeholder="Alex" className="mt-1 input-crisp" data-testid="gl-sender-first" />
            </div>
            <div>
              <Label className="overline">Sender full name</Label>
              <Input value={senderFull} onChange={(e) => setSenderFull(e.target.value)} placeholder="Alex Thompson" className="mt-1 input-crisp" data-testid="gl-sender-full" />
            </div>
          </div>
          <Button size="sm" onClick={() => apply({ sender_first_name: senderFirst, sender_full_name: senderFull }, "Sender name saved")} disabled={!senderFirst.trim() || !senderFull.trim()} data-testid="gl-save-sender" style={{ borderRadius: 2 }}>Save sender name</Button>

          <div className="pt-3 border-t border-border">
            <Label className="overline">Value proposition (AI seed for every drafted email)</Label>
            <textarea
              value={vp}
              onChange={(e) => setVp(e.target.value)}
              placeholder="Paste or use suggested below…"
              rows={8}
              className="mt-1 w-full input-crisp text-sm px-3 py-2 bg-background border border-border"
              style={{ borderRadius: 2 }}
              data-testid="gl-vp"
            />
            <div className="text-xs text-muted-foreground mt-1 flex items-center justify-between">
              <span>{vp.length > 0 ? `${vp.length} chars saved` : "Click 'Use suggested' to start with the AU-CFO template, then edit to your voice."}</span>
              <button onClick={() => { setVp(suggestedVp); toast.info("Suggested value prop loaded — edit and Save"); }} className="underline" data-testid="gl-vp-suggested">Use suggested</button>
            </div>
            <Button size="sm" onClick={() => apply({ value_proposition: vp }, "Value proposition saved")} disabled={!vp || vp.length < 50} className="mt-2" data-testid="gl-save-vp" style={{ borderRadius: 2 }}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Save value proposition
            </Button>
          </div>

          <div className="pt-3 border-t border-border">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="gl-auto-send-label">
              <Checkbox
                checked={(data.items.find((i) => i.id === "auto_send") || {}).status === "ready"}
                onCheckedChange={(v) => apply({ auto_send: !!v }, v ? "Auto-send ENABLED — GT Engine will fire emails on schedule" : "Auto-send disabled — drafts will wait for manual approval")}
                data-testid="gl-auto-send"
              />
              <div>
                <div className="text-sm">Auto-send drafted emails (no manual approval)</div>
                <div className="text-xs text-muted-foreground">When ON, GT Engine drafts + sends each sequence step automatically via Instantly. Turn OFF if you want to approve every email first.</div>
              </div>
            </label>
          </div>
        </div>
      </Section>

      {/* Sending block */}
      <Section title={CATEGORY_LABELS.sending} items={grouped.sending || []}>
        <div className="bg-card border border-border p-5" style={{ borderRadius: 2 }}>
          <div className="text-sm">
            <p>Mailbox warmup is auto-tracked from Instantly. No action required — these go green automatically when scores ≥ 80 and day ≥ 14.</p>
            <Link to="/deliverability" className="underline text-xs mt-2 inline-block">View live warmup health →</Link>
          </div>
        </div>
      </Section>

      {/* Sourcing block */}
      <Section title={CATEGORY_LABELS.sourcing} items={grouped.sourcing || []}>
        <div className="bg-card border border-border p-5 text-sm" style={{ borderRadius: 2 }}>
          Add or edit lead-sourcing keys in <Link to="/settings" className="underline">Settings</Link>. Create or activate Sales-Nav searches in <Link to="/lead-finder" className="underline">Lead Finder → Sales Nav</Link>.
        </div>
      </Section>

      {/* Booking block */}
      <Section title={CATEGORY_LABELS.booking} items={grouped.booking || []}>
        <div className="bg-card border border-border p-5 space-y-3" style={{ borderRadius: 2 }}>
          <Label className="overline">Booking method</Label>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2 cursor-pointer p-3 border border-border" style={{ borderRadius: 2 }} data-testid="gl-cal-outlook">
              <input type="radio" name="cal" value="outlook" checked={calMode === "outlook"} onChange={() => setCalMode("outlook")} className="mt-1" />
              <div className="flex-1">
                <div className="font-medium">Outlook / Calendly / Cal.com link (auto-attached to every "meeting ask" email)</div>
                <Input
                  value={bookingLink}
                  onChange={(e) => setBookingLink(e.target.value)}
                  placeholder="https://outlook.office.com/bookwithme/user/…"
                  className="mt-2 input-crisp font-mono text-xs"
                  disabled={calMode !== "outlook"}
                  data-testid="gl-cal-link"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Looking for it in Outlook? Click your profile in Outlook web → My Bookings → copy share URL.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-3 border border-border" style={{ borderRadius: 2 }} data-testid="gl-cal-manual">
              <input type="radio" name="cal" value="manual" checked={calMode === "manual"} onChange={() => setCalMode("manual")} className="mt-1" />
              <div>
                <div className="font-medium">Hand-schedule for the first 10 meetings</div>
                <div className="text-xs text-muted-foreground">Skip the booking link — reply manually with 2-3 time options when a CFO bites.</div>
              </div>
            </label>
          </div>
          <Button
            size="sm"
            onClick={() => apply(
              calMode === "outlook"
                ? { booking_link: bookingLink, calendar_mode: "outlook" }
                : { booking_link: null, calendar_mode: "manual" },
              "Booking method saved"
            )}
            disabled={calMode === "outlook" && (!bookingLink || !bookingLink.startsWith("http"))}
            data-testid="gl-save-cal"
            style={{ borderRadius: 2 }}
          >
            Save booking method
          </Button>
        </div>
      </Section>

      {/* Timeline */}
      <div className="bg-card border border-border p-6 mt-8" style={{ borderRadius: 2 }} data-testid="gl-timeline">
        <div className="overline mb-3">Cutover timeline</div>
        <div className="space-y-3 text-sm">
          <TimelineRow date="Now → Day 14" task="Warmup runs unattended. AI drafts queued. Saved-search scrapes drip new CFOs daily." status="active" />
          <TimelineRow date="Day 14-20" task="Final ramp. Tweak filters or add saved searches. Get to 200+ enrolments." status="upcoming" />
          <TimelineRow date={`Day 21 (~ ${eta(21)})`} task="Slow-ramp ends. Instantly fires Step 1 emails automatically. Replies start landing in /triage." status="upcoming" highlight />
          <TimelineRow date="Day 21+" task="Daily 15-min routine: check coach, triage replies, hand-schedule first ~10 booked meetings." status="upcoming" />
        </div>
      </div>
    </Layout>
  );
}

function Section({ title, items, children }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-6">
      <div className="overline mb-2">{title}</div>
      <div className="space-y-2 mb-3">
        {items.map((it) => <ChecklistItem key={it.id} item={it} />)}
      </div>
      {children}
    </div>
  );
}

function ChecklistItem({ item }) {
  const icon = item.status === "ready" ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
    : item.status === "warning" ? <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
    : <XCircle className="h-4 w-4 text-destructive" />;
  return (
    <div className="flex items-start gap-3 py-1.5" data-testid={`gl-item-${item.id}`}>
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">{item.label}</div>
        <div className="text-xs text-muted-foreground truncate">{item.value} {item.status !== "ready" && <span>· <span className="italic">{item.fix_text}</span></span>}</div>
      </div>
    </div>
  );
}

function TimelineRow({ date, task, status, highlight }) {
  return (
    <div className={`flex gap-3 ${highlight ? "bg-[hsl(var(--success))]/5 px-3 py-2 -mx-3" : ""}`} style={{ borderRadius: 2 }}>
      <div className="overline w-40 shrink-0 mt-0.5">{date}</div>
      <div className="text-sm flex-1">{task}</div>
    </div>
  );
}

function eta(daysFromStart) {
  // Approx: warmup started ~12 May 2026; day 21 ≈ 2 June
  const d = new Date(2026, 4, 11);  // May 11 2026
  d.setDate(d.getDate() + daysFromStart);
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}
